import { createIconElement, MENUITEM_IDS } from '@/lib';
import { useUser } from '@/queries/useUser';
import { useGlobalStore } from '@/stores';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BroadcastsTab, FlowOnboarding, FlowsTab, RecipientsTab } from '@/features/email-flows/components';
import { useStanRouter } from '@/hooks/useStanRouter';
import { StanButton, StanTab, StanTabContent, StanTabList, StanTabs } from '@/components';
import IcPlus from '@/assets/icons/ic-plus.svg';
import FlowsIcon from '@/assets/icons/ic-flows.svg';
import BroadcastsIcon from '@/assets/icons/ic-email-flows.svg';
import RecipientsIcon from '@/assets/icons/ic-community.svg';

const EmailFlowsPage = () => {
    const { user, isUserLoading, refetchUser } = useUser();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);
    const [searchParams, setSearchParams] = useSearchParams();
    const { push } = useStanRouter();
    const activeTab = searchParams.get('tab') || 'flows';

    useEffect(() => {
        setBreadcrumbs([{ title: 'My Email Flows', to: '/flows', active: true, id: MENUITEM_IDS.FLOWS }]);
    }, [setBreadcrumbs]);

    const handleTabChange = (tab: string) => {
        setSearchParams({ tab });
    };

    const handleAddFlow = () => {
        push('/flows/new');
    };

    const handleCreateEmail = () => {
        push('/flows/-1/broadcast/new');
    };

    // Check if email flows module is allowed (safe to check even while loading)
    const isEmailFlowsAllowed = user?.subscription_tier_allowed_modules?.includes('email-flows') || false;

    // Check if address is complete
    const isAddressComplete = Boolean(
        user?.data?.address?.city && user?.data?.address?.street && user?.data?.address?.postal_code
    );

    // Check if onboarding is needed (only if user data is loaded)
    const showOnboarding = user && (!user.tags?.email_flow_onboarding || !isAddressComplete);

    // Show Add Flow button only on flows tab when user has email flows allowed
    const showAddFlowButton = activeTab === 'flows' && isEmailFlowsAllowed && !isUserLoading;

    // Show Create Email button only on broadcasts tab when user has email flows allowed
    const showCreateEmailButton = activeTab === 'broadcasts' && isEmailFlowsAllowed && !isUserLoading;

    return (
        <>
            {/* Onboarding State - only show if explicitly needed */}
            {showOnboarding && (
                <FlowOnboarding
                    onComplete={async () => {
                        // Navigate to flows tab and refetch data
                        push('/flows?tab=flows');
                        await refetchUser();
                    }}
                />
            )}

            {/* Tabs with Add Flow Button - show immediately, tabs handle loading states */}
            {!showOnboarding && (
                <StanTabs value={activeTab} onValueChange={handleTabChange}>
                    <div className='mb-4 flex gap-4 max-sm:flex-col sm:mb-6 sm:h-12 sm:items-center sm:justify-between'>
                        <StanTabList>
                            <StanTab value='flows'>
                                <span className='flex items-center gap-2'>
                                    {createIconElement(FlowsIcon, { className: 'size-5' })}
                                    <span>Flows</span>
                                </span>
                            </StanTab>
                            <StanTab value='broadcasts'>
                                <span className='flex items-center gap-2'>
                                    {createIconElement(BroadcastsIcon, { className: 'size-5' })}
                                    <span>Broadcasts</span>
                                </span>
                            </StanTab>
                            <StanTab value='recipients'>
                                <span className='flex items-center gap-2'>
                                    {createIconElement(RecipientsIcon, { className: 'size-5' })}
                                    <span>Recipients</span>
                                </span>
                            </StanTab>
                        </StanTabList>

                        {/* Tab-specific action buttons */}
                        {showAddFlowButton && (
                            <StanButton name='add-flow-button' onClick={handleAddFlow} icon={IcPlus}>
                                Add Flow
                            </StanButton>
                        )}
                        {showCreateEmailButton && (
                            <StanButton name='create-email-button' onClick={handleCreateEmail} icon={IcPlus}>
                                Create Email
                            </StanButton>
                        )}
                    </div>

                    <StanTabContent value='flows'>
                        <FlowsTab isEmailFlowsAllowed={isEmailFlowsAllowed} isUserLoading={isUserLoading} />
                    </StanTabContent>
                    <StanTabContent value='broadcasts'>
                        <BroadcastsTab isEmailFlowsAllowed={isEmailFlowsAllowed} isUserLoading={isUserLoading} />
                    </StanTabContent>
                    <StanTabContent value='recipients'>
                        <RecipientsTab isUserLoading={isUserLoading} />
                    </StanTabContent>
                </StanTabs>
            )}
        </>
    );
};

export default EmailFlowsPage;
